// ============================================================
//  VideoRecorderModal — ACTIVE / WORKING VERSION
//  Uses: tinyFaceDetector + jaw-based gaze logic (original)
// ============================================================

import React, { useEffect, useRef, useState } from "react";
import { Box } from "@mui/material";
import * as faceapi from "face-api.js";
import { useNavigate } from "react-router-dom";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import { interviewApi } from "../../api/interviewApi";
import { flowwiseApi } from "../../api/flowiseApi";
import { proctoringApi } from "../../api/proctoringApi";
import { useAuth } from "../../contexts/AuthContext";

const VideoRecorderModal = ({ open, candidateId, onBeforeNavigate }) => {
    const { upload_id, interview_id } = useAuth();

    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const streamRef = useRef(null);
    const recorderRef = useRef(null);

    const intervalRef = useRef(null);
    const detectionIntervalRef = useRef(null);

    const violationLocked = useRef(false);
    const cooldownRef = useRef(0);
    const faceViolationCooldownRef = useRef(0);
    const modelsLoadedRef = useRef(false);

    const [showWarning, setShowWarning] = useState(false);
    const [violationCount, setViolationCount] = useState(0);
    const [lastViolationType, setLastViolationType] = useState(null);
    const [lastViolationCount, setLastViolationCount] = useState(0);

    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "",
    });

    const chunkIndexRef = useRef(1);

    const TAB_SWITCH_LIMIT = 3;
    const CHUNK_INTERVAL = 25000;

    const navigate = useNavigate();

    const showSnackbar = (message, severity = "info") => {
        setSnackbar({
            open: true,
            message,
            severity,
        });
    };

    // ---------------- FACE MODELS ----------------
    const loadFaceModels = async () => {
        if (modelsLoadedRef.current) return;

        try {
            await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
            await faceapi.nets.faceLandmark68Net.loadFromUri("/models");
            modelsLoadedRef.current = true;
            // console.log("✅ face-api models loaded");
        } catch (err) {
            console.error("❌ face-api model load failed:", err);
        }
    };

    // ---------------- FACE MONITORING ----------------
    const startFaceMonitoring = () => {
        if (detectionIntervalRef.current) {
            clearInterval(detectionIntervalRef.current);
        }

        detectionIntervalRef.current = setInterval(async () => {
            try {
                if (!videoRef.current || !modelsLoadedRef.current) return;
                if (document.hidden) return;
                if (violationLocked.current) return;

                const now = Date.now();

                if (now - faceViolationCooldownRef.current < 2500) return;

                const detections = await faceapi
                    .detectAllFaces(
                        videoRef.current,
                        new faceapi.TinyFaceDetectorOptions({
                            inputSize: 320,
                            scoreThreshold: 0.5,
                        })
                    )
                    .withFaceLandmarks();

                // MULTIPLE FACES
                if (detections.length > 1) {
                    faceViolationCooldownRef.current = now;
                    handleViolation("MULTIPLE_FACES");
                    return;
                }

                // LOOKING AWAY (jaw-based deviation — original logic)
                const landmarks = detections[0].landmarks;
                const jaw = landmarks.getJawOutline();
                const nose = landmarks.getNose()[3];

                const leftJaw = jaw[0];
                const rightJaw = jaw[16];
                const centerX = (leftJaw.x + rightJaw.x) / 2;

                const deviation = Math.abs(nose.x - centerX);

                // (detection commented out intentionally — kept for reference)
                // if (deviation > 22) {
                //     faceViolationCooldownRef.current = now;
                //     handleViolation("HEAD_POSE");
                // }
            } catch (err) {
                console.warn("face detection error:", err);
            }
        }, 2200);
    };

    // ---------------- CAMERA ----------------
    const startCamera = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: true,
                audio: true,
            });

            window.activeInterviewStream = stream;
            streamRef.current = stream;

            videoRef.current.srcObject = stream;
            await videoRef.current.play();

            await loadFaceModels();
            startFaceMonitoring();

            startChunkRecording();
        } catch (error) {
            console.error("Camera error:", error);
        }
    };

    const stopCamera = () => {
        try {
            if (recorderRef.current) {
                try {
                    recorderRef.current.ondataavailable = null;
                    if (recorderRef.current.state !== "inactive") {
                        recorderRef.current.stop();
                    }
                } catch (e) { }
            }

            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => {
                    try {
                        track.stop();
                    } catch (e) { }
                });
            }

            if (videoRef.current) {
                videoRef.current.srcObject = null;
            }

            window.activeInterviewStream = null;
        } catch (err) {
            console.error("stopCamera error:", err);
        }

        if (intervalRef.current) clearInterval(intervalRef.current);
        if (detectionIntervalRef.current)
            clearInterval(detectionIntervalRef.current);

        streamRef.current = null;
        recorderRef.current = null;
    };

    // ---------------- RECORDING ----------------
    const startChunkRecording = () => {
        try {
            const recorder = new MediaRecorder(streamRef.current, {
                mimeType: "video/webm; codecs=vp8",
                videoBitsPerSecond: 5_000_000,
            });

            recorderRef.current = recorder;

            recorder.ondataavailable = async (event) => {
                try {
                    if (!event.data || event.data.size < 50000) return;

                    const chunkIndex = chunkIndexRef.current++;

                    const file = new File(
                        [event.data],
                        `candidate_${candidateId}_chunk_${chunkIndex}.webm`,
                        { type: "video/webm" }
                    );

                    const formData = new FormData();
                    formData.append("candidate_id", candidateId);
                    formData.append("upload_id", upload_id);
                    formData.append("chunk_index", chunkIndex);
                    formData.append("interview_id", interview_id);
                    formData.append("file", file);

                    await interviewApi.interviewVideoUpload(formData);
                } catch (error) {
                    console.error("Upload exception:", error);
                }
            };

            recorder.start();

            if (intervalRef.current) clearInterval(intervalRef.current);

            intervalRef.current = setInterval(() => {
                try {
                    if (recorder.state === "recording") {
                        recorder.requestData();
                    }
                } catch (err) {
                    console.error("requestData error:", err);
                }
            }, CHUNK_INTERVAL);
        } catch (err) {
            console.error("startChunkRecording error:", err);
        }
    };

    // ---------------- FLUSH LAST CHUNK ----------------
    const flushLastChunk = async () => {
        return new Promise((resolve) => {
            try {
                const recorder = recorderRef.current;

                if (!recorder || recorder.state !== "recording") {
                    resolve();
                    return;
                }

                recorder.requestData();

                setTimeout(() => {
                    try {
                        recorder.stop();
                    } catch (err) { }

                    resolve();
                }, 250);
            } catch (err) {
                resolve();
            }
        });
    };

    // ---------------- VIOLATION ----------------
    const handleViolation = (type) => {
        if (violationLocked.current) return;

        const now = Date.now();
        if (now - cooldownRef.current < 900) return;

        cooldownRef.current = now;

        // 🔥 Backend Log API Call
        proctoringApi.logViolation({
            violation_type: type,
            metadata: "",
            source: "webcam",
        });

        // Disable popups for gaze and head pose (just log to backend)
        if (type === "GAZE" || type === "HEAD_POSE") {
            return;
        }

        setViolationCount((prev) => {
            const newCount = prev + 1;

            setLastViolationCount(newCount);
            setLastViolationType(type);

            if (newCount < TAB_SWITCH_LIMIT) {
                setShowWarning(true);
            }

            if (newCount >= TAB_SWITCH_LIMIT) {
                violationLocked.current = true;
                setShowWarning(false);
                terminateInterview();
            }

            // if (newCount === TAB_SWITCH_LIMIT - 1) {
            //     tryCheating("TabSwitch");
            // }

            return newCount;
        });
    };

    // ---------------- EVENTS ----------------
    useEffect(() => {
        const onHidden = () =>
            document.hidden && handleViolation("TAB_SWITCH");

        document.addEventListener("visibilitychange", onHidden);

        return () =>
            document.removeEventListener("visibilitychange", onHidden);
    }, []);

    // ---------------- OPEN / CLOSE ----------------
    useEffect(() => {
        if (!open) {
            stopCamera();
            return;
        }

        startCamera();

        return () => {
            stopCamera();
        };
    }, [open]);

    useEffect(() => {
        const handler = async () => {
            await flushLastChunk();
            stopCamera();
        };

        window.addEventListener("flush-final-video-chunk", handler);

        return () => {
            window.removeEventListener("flush-final-video-chunk", handler);
        };
    }, []);

    // ---------------- TERMINATE ----------------
    const terminateInterview = async () => {
        if (!violationLocked.current) violationLocked.current = true;

        stopCamera();

        await new Promise((r) => setTimeout(r, 80));
        await flushLastChunk().catch(() => { });

        try {
            const payload = {
                "reason": lastViolationType || "policy_violation",
            }
            const response = await interviewApi.terminate(payload)
            if (typeof onBeforeNavigate === 'function') {
                onBeforeNavigate();
            }
            navigate("/restriction", {
                state: {
                    reason: "tab_switch_exceeded",
                },
                replace: true,
            });
        } catch (error) {
            console.error(error)
            navigate("/restriction", {
                state: {
                    reason: "tab_switch_exceeded",
                },
                replace: true,
            });
        }
    };

    // ---------------- TERMINATE INTERVIEW (INSTANT) ----------------
    const tryCheating = async () => {
        const payload = {
            interview_id: interview_id,
            reason: "TabSwitch",
        };
        try {
            const resp = await flowwiseApi.cheatingTryApi(payload);
            const { status, message } = resp || {};

            if (status === 0) {
                showSnackbar(
                    "Network error. Please check your connection.",
                    "warning"
                );
                return;
            }
            if (status === 200) {
                return;
            }
            console.error(message);
            showSnackbar("Unexpected response from server.", "error");
        } catch (error) {
            showSnackbar("Upload failed. Please try again.", "error");
        }
    };

    return (
        <>
            {open && (
                <Box
                    sx={{
                        position: "fixed",
                        top: 56,
                        right: 20,
                        width: 210,
                        height: 150,
                        bgcolor: "black",
                        borderRadius: 2,
                        boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                        zIndex: 1000,
                        border: "3px solid #1a237e",
                        overflow: "hidden",
                    }}
                >
                    <video
                        ref={videoRef}
                        autoPlay
                        muted
                        playsInline
                        style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                            display: "block",
                        }}
                    />

                    <Box
                        sx={{
                            position: "absolute",
                            top: 8,
                            right: 8,
                            bgcolor: "rgba(0,0,0,0.7)",
                            color: "white",
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: "0.7rem",
                            fontWeight: 600,
                        }}
                    >
                        🔴 LIVE
                    </Box>
                </Box>
            )}

            {open &&
                showWarning &&
                violationCount < TAB_SWITCH_LIMIT && (
                    <Box
                        sx={{
                            position: "fixed",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            bgcolor: "rgba(0,0,0,0.75)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            zIndex: 1400,
                        }}
                        onClick={() => setShowWarning(false)}
                    >
                        <Box
                            sx={{
                                width: 400,
                                bgcolor: "white",
                                p: 3.5,
                                borderRadius: 3,
                                textAlign: "center",
                                boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                            }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3
                                style={{
                                    margin: 0,
                                    marginBottom: 16,
                                    color: "#d32f2f",
                                }}
                            >
                                ⚠️ Warning
                            </h3>

                            <div
                                style={{
                                    marginBottom: 20,
                                    lineHeight: 1.6,
                                }}
                            >
                                {lastViolationType === "TAB_SWITCH" &&
                                    "You switched tabs during the interview."}

                                {lastViolationType === "GAZE" &&
                                    "You moved away from the interview window."}

                                {lastViolationType === "NO_FACE" &&
                                    "Your face is not visible on camera."}

                                {lastViolationType === "MULTIPLE_FACES" &&
                                    "Multiple people detected in camera."}

                                {lastViolationType === "HEAD_POSE" &&
                                    "Please keep looking at the screen."}

                                <br />

                                <strong style={{ color: "#d32f2f" }}>
                                    Violation: {lastViolationCount}/
                                    {TAB_SWITCH_LIMIT}
                                </strong>
                            </div>

                            <button
                                onClick={() => setShowWarning(false)}
                                style={{
                                    padding: "10px 24px",
                                    borderRadius: 8,
                                    border: "none",
                                    backgroundColor: "#1a237e",
                                    color: "white",
                                    fontSize: "1rem",
                                    fontWeight: 600,
                                    cursor: "pointer",
                                }}
                            >
                                I Understand
                            </button>
                        </Box>
                    </Box>
                )}

            <canvas ref={canvasRef} style={{ display: "none" }} />

            {/* SNACKBAR */}
            <CustomSnackbar
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={() =>
                    setSnackbar((s) => ({
                        ...s,
                        open: false,
                    }))
                }
            />
        </>
    );
};

export default VideoRecorderModal;


// ============================================================
//
//  Reason: @tensorflow/tfjs conflicts with face-api.js's
//  internal @tensorflow/tfjs-core version (see error notes below)
//
//  HOW TO RE-ENABLE:
//  1. Uninstall face-api.js
//  2. Install @vladmandic/face-api (maintained fork with modern TF.js support)
//  3. Pin: @tensorflow/tfjs@3.x + @tensorflow-models/coco-ssd@2.x
//  4. Uncomment the block below and delete the active version above
// ============================================================

// import * as tf from "@tensorflow/tfjs";
// import * as cocoSsd from "@tensorflow-models/coco-ssd";
//
// const PROHIBITED_CLASSES = new Set(["cell phone", "laptop", "book"]);
// const OBJECT_CONFIDENCE  = 0.65;
// const SUSPICION_THRESHOLD = 3;
// const RATIO_MIN = 0.5;
// const RATIO_MAX = 2.0;
// const FACE_INTERVAL   = 2_200;
// const OBJECT_INTERVAL = 4_000;
//
// const cocoModelRef        = useRef(null);
// const faceSuspicionRef    = useRef(0);
// const objectSuspicionRef  = useRef(0);
// const objectIntervalRef   = useRef(null);
//
// const loadCocoModel = async () => {
//     if (cocoModelRef.current) return;
//     await tf.ready();
//     cocoModelRef.current = await cocoSsd.load();
// };
//
// // ssdMobilenetv1 face detection
// const detections = await faceapi
//     .detectAllFaces(videoRef.current,
//         new faceapi.SsdMobilenetv1Options({ minConfidence: 0.5 }))
//     .withFaceLandmarks();
//
// // Nose-to-Eye Ratio head pose
// // ratio = |nose.x - leftEye.x| / |rightEye.x - nose.x|
// // Flag HEAD_POSE if ratio < 0.5 OR ratio > 2.0
//
// // Object detection (every 4s, staggered)
// // Fires PROHIBITED_OBJECT after 3 consecutive detections